import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type {
  ApiResponse, BvDaysMapping, BvTimesMapping, BvMonthsMapping,
  BvTimeType, BvTariffTreatment, TreatmentFunction, BvEvent,
} from '../types/billValidationTypes';

/**
 * CRUD for the global tariff vocabulary tables. These carry no org_id — they are
 * shared reference data like `holidays` and `retailer_tou_rates`, so there is no
 * organisation filter here.
 */
function makeVocabService<T extends { id: string; sort_order: number }>(table: string) {
  return {
    list: async (): Promise<ApiResponse<T[]>> => {
      try {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .order('sort_order', { ascending: true })
          .order('label', { ascending: true });
        if (error) throw error;
        return { success: true, data: (data ?? []) as T[] };
      } catch (e: unknown) {
        return { success: false, error: (e as Error).message };
      }
    },

    create: async (row: Partial<T>): Promise<ApiResponse<T>> => {
      try {
        const { data, error } = await supabase.from(table).insert(row).select().single();
        if (error) throw error;
        return { success: true, data: data as T };
      } catch (e: unknown) {
        return { success: false, error: (e as Error).message };
      }
    },

    update: async (id: string, patch: Partial<T>): Promise<ApiResponse<T>> => {
      try {
        const { data, error } = await supabase
          .from(table)
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return { success: true, data: data as T };
      } catch (e: unknown) {
        return { success: false, error: (e as Error).message };
      }
    },

    remove: async (id: string): Promise<ApiResponse<null>> => {
      try {
        const { error } = await supabase.from(table).delete().eq('id', id);
        // bv_site_network references these with ON DELETE RESTRICT, so deleting a
        // row still in use fails here rather than silently unmapping rate rows.
        if (error) {
          if (error.code === '23503') {
            return {
              success: false,
              error: 'Still used by one or more tariff rates — reassign those first.',
            };
          }
          throw error;
        }
        return { success: true, data: null };
      } catch (e: unknown) {
        return { success: false, error: (e as Error).message };
      }
    },
  };
}

export const daysMappingService   = makeVocabService<BvDaysMapping>('bv_days_mapping');
export const timesMappingService  = makeVocabService<BvTimesMapping>('bv_times_mapping');
export const monthsMappingService = makeVocabService<BvMonthsMapping>('bv_months_mapping');
export const timeTypeService      = makeVocabService<BvTimeType>('bv_time_type');
export const treatmentService     = makeVocabService<BvTariffTreatment>('bv_tariff_treatment');

/** Available bv_treat_* functions, for the treatment form's Function dropdown. */
export const listTreatmentFunctions = async (): Promise<ApiResponse<TreatmentFunction[]>> => {
  try {
    const { data, error } = await supabase.rpc('bv_list_treatment_functions');
    if (error) throw error;
    return { success: true, data: (data ?? []) as TreatmentFunction[] };
  } catch (e: unknown) {
    return { success: false, error: (e as Error).message };
  }
};

// ----------------------------------------------------------------------------
// bv_events — critical network event windows.
//
// Unlike the five vocabulary tables above, `bv_events` is org-scoped (which org
// loaded which events is not global) and unbounded (an event list grows with
// time while a vocabulary does not), so `eventService` differs from the vocab
// sub-services in two ways: every method filters by `org_id`, and `list` takes
// an optional date range to bound the result set. The return shape and error
// handling mirror `makeVocabService` exactly so the admin UI's `VocabularyTab`
// consumes `eventService` generically.
// ----------------------------------------------------------------------------

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

export const eventService = {
  list: async (opts?: { from?: string; to?: string }): Promise<ApiResponse<BvEvent[]>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organisation not found' };
      let query = supabase
        .from('bv_events')
        .select('*')
        .eq('org_id', orgId)
        .order('event_date', { ascending: false })
        .order('time_start', { ascending: true });
      if (opts?.from) query = query.gte('event_date', opts.from);
      if (opts?.to) query = query.lte('event_date', opts.to);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: (data ?? []) as BvEvent[] };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  create: async (row: Partial<BvEvent>): Promise<ApiResponse<BvEvent>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organisation not found' };
      const { data, error } = await supabase
        .from('bv_events')
        .insert({ ...row, org_id: orgId })
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: data as BvEvent };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  update: async (id: string, patch: Partial<BvEvent>): Promise<ApiResponse<BvEvent>> => {
    try {
      const { data, error } = await supabase
        .from('bv_events')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data: data as BvEvent };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },

  remove: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const { error } = await supabase.from('bv_events').delete().eq('id', id);
      if (error) throw error;
      return { success: true, data: null };
    } catch (e: unknown) {
      return { success: false, error: (e as Error).message };
    }
  },
};